import { Box, Stack, Typography, List, ListItem } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { getField, formatNumber } from 'src/common/helper';
import dayjs from 'dayjs';
import { getLabelFromPatientSelect } from 'src/components/quotations/QuotationService';
import PrintInscriptionService from 'src/components/quotations/printQuote/PrintConsentFormService';

const fontText = {
  fontSize: '10px',
  fontWeight: '400',
  lineHeight: '1.5'
};

const groupBox = {
  border: '1px solid #000000',
  minHeight: '26.5mm'
};

const spaceRow20px = {
  mt: '4.8mm'
};

type PrintReceiptProps = {
  parts: any[];
  items: any[];
  detailData: any;
};

const CustomListItem = (text: string) => {
  return (
  <ListItem sx={{ p: '0' }}>
    <Typography sx={{ ...fontText, display:'flex', alignItems: 'start' }}>
      <FiberManualRecordIcon  sx={{ fontSize: '10px', pt: '5px' }} />
      { text }
    </Typography>
  </ListItem>
  );
};

const PrintReceipt: React.FC<PrintReceiptProps> = (props) => {

  return (
    <Box sx={{
      width: '100%',
      '@media print': {
        p: '4mm',
      }
    }}>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="flex-end"
        sx={{ height: '100%', width: '100%' }}
      >
        <img src="/images/print/header.png" alt="" height='10%' width='10%' />
      </Box>
      <Box sx={{ px: '8mm' }}>
        <Stack spacing={'2.8mm'}>
          <Box display={'flex'} justifyContent={'center'}>
            <Typography sx={{ ...fontText, fontSize: '28px' }}>
              モニター制度規約
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ ...fontText }}>
              モニター制度とは、お客様の美容整形手術や美容治療に関する写真や動画の撮影・体験談記入等にご協力いただくことにより、特別な価格で手術や治療を実施する制度です。施術の部分的なパーツを露出する<strong>部分モニター</strong>とお顔全体を露出する<strong>顔出しモニター</strong>があり、撮影対象範囲が変わります。なお部分モニターにおける、使用するパーツに関しては、指定できないものとなります。
            </Typography>
          </Box>

          <Box>
            <Typography sx={{ ...fontText, fontSize: '12px', fontWeight: 'bold' }}>
              【モニター条件】
            </Typography>
            <List>
              {CustomListItem('医師の診察の結果、モニターへのお申し込みをお断りする場合があります。')}
              {CustomListItem('お客様の虚偽の申告等、お客様都合により治療が中止となった場合、違約金として下記の正規料金をお支払いただきます。')}
              {CustomListItem('対象施術後６ヵ月間は、美容に関する施術を他院で受けないことをお約束いただきます。ただし、当院が許可した場合はこの限りではありません。又、術後１年以内に他院で施術・手術等を受けられる場合は、事前に担当医へご相談下さい。')}
              {CustomListItem('下記の日程で、写真撮影、動画撮影にご対応いただきます。なお、撮影はメイク有り・無し両方での撮影になります。（施術直後などメイクができない場合は、メイク無しの撮影のみとなります。）')}
            </List>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ height: '100%', width: '100%', my: '4mm' }}
            >
              <img src="/images/print/photo_schedule.png" alt=""/>
            </Box>
            <List>
              {CustomListItem('写真撮影、動画撮影、アンケートの作成等の日程は、経過により追加や変更になる事があります。')}
              {CustomListItem('写真撮影、動画撮影の対象範囲は、部分モニターの場合は施術部位を中心とした一部となります。全顔モニターの場合は施術部位に関係なく顔全体が掲載されます。')}
              {CustomListItem('施術後のHIFU、フィラー治療、イメージコンサルティング施術（メイク、アートメイク等）を行った場合は本モニターの対象範囲となります。')}
              {CustomListItem('撮影した写真や動画の公開先は、当該治療施設および(株)KYイメージングテックが企画する広告全て（例:テレビ・WEBページ・SNS・雑誌・チラシ・パンフレット・ポスター・第三者利用・学術活動等）とし、使用期限は無期限です。')}
              {CustomListItem('交通費、検査代、宿泊費、麻酔代は割引対象外です。')}
            </List>
          </Box>


          <Box display={'flex'} justifyContent={'center'}>
            <Typography sx={{ ...fontText, fontSize: '28px' }}>
              モニター制度　申し込み
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ ...fontText }}>
              { '本規約に記載された内容を理解し同意した上で、モニターとして下記の施術に申し込みます。' }
            </Typography>
            <Typography sx={{ ...fontText }}>
              { '本規約を守らなかった場合は、下記の正規料金を支払います。' }
            </Typography>

            <Box sx={{ ...groupBox, p: '2.4mm 2mm', width: '75%', m: 'auto', mt: '4mm' }}>
              <Box display={'flex'} justifyContent={'row'}>
                <Typography sx={{ ...fontText, mr: '64mm' }}>
                ・施術日：{dayjs(getField(props.detailData, 'reserve_date')).format('YYYY年MM月DD日')}
                </Typography>
                <Typography sx={{ ...fontText }}>
                ・担当医：{getField(props.detailData, 'doctor')}
                </Typography>
              </Box>
              <Box sx={{ minHeight: '22mm' }}>
                <Typography sx={{ ...fontText }}>・施術内容：</Typography>
                <Box sx={{ ml: '10px' }}>
                {props.items
                  .filter((item: any) => item['surgery']['surgery'] && PrintInscriptionService.canDisplaySurgery(getField(item.surgery, 'surgery')))
                  .map((item: any, index: number, filteredItems) => (
                    <Box key={index} display={'inline-flex'}>
                      <Typography sx={{ ...fontText }} mr={'1.2mm'}>
                        {getField(item.surgery, 'surgery') +
                          (getField(item, 'note', '') ? '（' + getField(item, 'note') + '）' : '') +
                          (index < filteredItems.length - 1 ? '、' : '')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                
              </Box>
              <Typography sx={{ ...fontText }}>
              ・区分：{getLabelFromPatientSelect(getField(props.detailData, 'patient_select', 1))}
              </Typography>
              <Typography sx={{ ...fontText }}>
              ・正規料金：{formatNumber(getField(props.detailData, 'original_total')) + '円'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: '32mm' }}>
            <Box display={'flex'} justifyContent={'flex-end'} alignItems={'flex-end'} sx={{ ...spaceRow20px }}>
              <Typography component={'pre'} sx={{ ...fontText, ml: '21.166mm' }}>
                {'年'}
              </Typography>
              <Typography component={'pre'} sx={{ ...fontText, ml: '7.9375mm' }}>
                {'月'}
              </Typography>
              <Typography component={'pre'} sx={{ ...fontText, ml: '7.9375mm' }}>
                {'日'}
              </Typography>
            </Box>

            <Box display={'flex'} justifyContent={'flex-start'} alignItems={'flex-end'} sx={{ ...spaceRow20px }}>
              <Typography component={'pre'} sx={{ ...fontText }}>
                {'電話番号: '}
              </Typography>
            </Box>

            <Box display={'flex'} justifyContent={'flex-start'} alignItems={'flex-end'} sx={{ ...spaceRow20px }}>
              <Typography component={'pre'} sx={{ ...fontText }}>
                {'本人署名: '}
              </Typography>
            </Box>
          </Box>

          <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ height: '100%', width: '100%'}}
            >
            <img src="/images/print/footer.png" alt=""/>
          </Box>

        </Stack>
      </Box >
    </Box >
  );
}

export default PrintReceipt;
